import { gql } from '@apollo/client';
import { Alert, ButtonGroup, Flex, Heading, mediaBreakpointMixin, token, withToast } from '@zillow/constellation';
import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import useQueryVariables from '../../../hooks/useQueryVariables';
import Applicants from './Applicants';
import Household from './Household';
import Pets from './Pets';
import { getNeedsToIdVerifySelector } from '../../../selectors';
import MessageRachelManager from '../MessageRachelManager';
import { CardSpinner } from '../../../components/CardSpinner';
import { PAGE_DATA_ERROR } from '../../../constants/stringConstants';
import { useZgGraphqlQuery } from '../../../hooks/useGraphqlQuery';
import DownloadApplicationButton from '../DownloadApplicationButton';

export const QUERY = gql`
  query ApplicationRollup($listingAlias: ID!, $applicationId: ID!, $needsToIdVerify: Boolean!) {
    rentalListing(id: $listingAlias) {
      id
      monthlyPayment
      landlordRentalApplication(id: $applicationId) {
        id
        applicants {
          id
          userId
          zgAuthZone
          isPrimaryApplicant
          status
          submittedDateTime
          landlordInvitation {
            firstName
            lastName
          }
          coapplicantInvitation {
            firstName
            lastName
          }
          answers {
            firstName
            middleName
            lastName
            moveInDate
            isMoveInDateFlexible
            isSmoker
            occupants
            pets {
              smallDogs
              mediumDogs
              largeDogs
              cats
              other
              description
            }
            currentIncomes {
              id
              type
              amount
              areDocumentsExpired @skip(if: $needsToIdVerify)
              documents {
                id
                expirationDateTime
              }
            }
          }
        }
        invitations {
          id
          email
          firstName
          lastName
          invitedDateTime
        }
      }
    }
  }
`;

const StyledApplicantsHeaderContainer = styled(Flex)`
  @media ${mediaBreakpointMixin('lg')} {
    border-bottom: 1px solid ${token('colors.borderLight')};
  }
`;

function ApplicationRollup() {
  const { applicationId, propertyId: listingAlias } = useQueryVariables();
  const needsToIdVerify = useSelector(getNeedsToIdVerifySelector());
  const { data, isLoading, isError } = useZgGraphqlQuery(QUERY, {
    listingAlias,
    applicationId,
    needsToIdVerify,
  });

  if (isLoading) {
    return <CardSpinner />;
  }

  if (isError) {
    return <Alert appearance="warning">{PAGE_DATA_ERROR}</Alert>;
  }

  return (
    <Flex display="flex" flexDirection="column">
      <StyledApplicantsHeaderContainer
        display="flex"
        justifyContent="space-between"
        alignItems={{ default: 'stretch', lg: 'center' }}
        flexDirection={{ default: 'column', lg: 'row' }}
        marginBottom="md"
        paddingBottom={{ lg: 'md' }}
      >
        <Heading level={4} as="h2" id="applicants" marginBottom={{ default: 'md', lg: 'unset' }}>
          Applicants
        </Heading>
        <ButtonGroup aria-label="Buttons to download application PDF and view messages">
          <DownloadApplicationButton />
          <MessageRachelManager />
        </ButtonGroup>
      </StyledApplicantsHeaderContainer>
      <Applicants
        marginBottom="xl"
        applicants={data.rentalListing.landlordRentalApplication.applicants}
        invitations={data.rentalListing.landlordRentalApplication.invitations}
      />
      <Flex
        display="flex"
        justifyContent="space-between"
        alignItems={{ default: 'flex-start', md: 'center' }}
        flexDirection={{ default: 'column', md: 'row' }}
        marginBottom="sm"
      >
        <Heading level={4} as="h2" marginBottom={{ default: 'md', md: 'unset' }}>
          Household summary
        </Heading>
      </Flex>
      <Household marginBottom="md" rentalListing={data.rentalListing} />
      <Pets applicants={data.rentalListing.landlordRentalApplication.applicants} />
    </Flex>
  );
}

ApplicationRollup.propTypes = {
  enqueueToast: PropTypes.func.isRequired,
};

export default withToast(ApplicationRollup);
